import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkle, TrendUp, ArrowsClockwise, Shield, WarningCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_STYLES = {
  critical: { bg: "bg-red-500/15", border: "border-red-500/30", text: "text-red-400", dot: "bg-red-400" },
  high:     { bg: "bg-orange-500/15", border: "border-orange-500/30", text: "text-orange-400", dot: "bg-orange-400" },
  medium:   { bg: "bg-amber-500/15", border: "border-amber-500/30", text: "text-amber-400", dot: "bg-amber-400" },
  low:      { bg: "bg-emerald-500/15", border: "border-emerald-500/30", text: "text-emerald-400", dot: "bg-emerald-400" },
};

export function RiskBadgeInline({ deviceName, datasetId, compact = false }) {
  const [risk, setRisk] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!deviceName) { setLoading(false); return; }
    let active = true;
    axios.get(`${API}/ai/risk-score`, { params: { device_name: deviceName, dataset_id: datasetId } })
      .then((res) => { if (active) setRisk(res.data); })
      .catch(() => { if (active) setRisk(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [deviceName, datasetId]);

  if (loading) {
    return <Badge variant="outline" className="border-zinc-700 text-[10px] font-mono text-zinc-500" data-testid="risk-badge-loading">…</Badge>;
  }
  if (!risk) return null;
  const style = LEVEL_STYLES[risk.risk_level] || LEVEL_STYLES.low;

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] font-mono ${style.bg} ${style.border} ${style.text}`}
        data-testid={`risk-badge-${deviceName}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
        {risk.risk_score}
      </span>
    );
  }

  return (
    <Badge className={`${style.bg} ${style.border} ${style.text} border text-[10px] font-mono uppercase tracking-wider gap-1`}
      data-testid={`risk-badge-${deviceName}`}>
      <Shield size={10} weight="duotone" />
      Risk {risk.risk_score} · {risk.risk_level}
    </Badge>
  );
}

export function RiskPredictionPanel({ deviceName, datasetId }) {
  const [risk, setRisk] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [loadingRisk, setLoadingRisk] = useState(true);
  const [loadingPred, setLoadingPred] = useState(false);
  const [error, setError] = useState(null);

  const fetchRisk = useCallback(async () => {
    if (!deviceName) { setLoadingRisk(false); return; }
    setLoadingRisk(true);
    try {
      const res = await axios.get(`${API}/ai/risk-score`, {
        params: { device_name: deviceName, dataset_id: datasetId },
      });
      setRisk(res.data);
    } catch (e) {
      setRisk(null);
    } finally {
      setLoadingRisk(false);
    }
  }, [deviceName, datasetId]);

  useEffect(() => { fetchRisk(); }, [fetchRisk]);

  const handlePredict = async () => {
    setLoadingPred(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/ai/predict-excursions`, {
        device_name: deviceName,
        dataset_id: datasetId,
        hours_ahead: 24,
      });
      setPrediction(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Prediction failed");
    } finally {
      setLoadingPred(false);
    }
  };

  const style = risk ? (LEVEL_STYLES[risk.risk_level] || LEVEL_STYLES.low) : LEVEL_STYLES.low;

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4" data-testid="risk-prediction-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-1.5">
          <Shield size={12} weight="duotone" className="text-teal-400" />
          AI Risk Score
        </h3>
        <button onClick={fetchRisk} className="text-zinc-500 hover:text-teal-400 transition-colors" title="Refresh"
          data-testid="risk-refresh-btn">
          <ArrowsClockwise size={12} className={loadingRisk ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Risk score */}
      {loadingRisk ? (
        <div className="text-xs font-mono text-zinc-500 animate-pulse">Calculating risk...</div>
      ) : !risk ? (
        <div className="text-xs text-zinc-600">No data available</div>
      ) : (
        <div className="space-y-3">
          <div className={`flex items-center justify-between p-3 rounded-sm border ${style.bg} ${style.border}`}>
            <div>
              <p className={`text-2xl font-mono font-bold ${style.text}`} data-testid="risk-score-value">
                {risk.risk_score}
                <span className="text-xs text-zinc-500 ml-1">/100</span>
              </p>
              <p className={`text-[10px] uppercase tracking-wider mt-0.5 ${style.text}`}>{risk.risk_level} risk</p>
            </div>
            <div className={`w-2 h-12 rounded-full ${style.dot}`} />
          </div>

          {/* Factors */}
          {risk.risk_factors?.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Contributing factors</p>
              {risk.risk_factors.slice(0, 4).map((f, i) => {
                const fStyle = LEVEL_STYLES[f.severity] || LEVEL_STYLES.medium;
                return (
                  <div key={i} className="flex items-start gap-2 text-[11px]">
                    <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${fStyle.dot}`} />
                    <div className="flex-1">
                      <span className="font-mono text-zinc-300 capitalize">{f.factor.replace("_", " ")}: </span>
                      <span className="text-zinc-500">{f.detail}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Predict CTA */}
      <div className="pt-3 border-t border-zinc-800">
        <Button onClick={handlePredict} disabled={loadingPred || !deviceName}
          className="w-full bg-teal-500/10 hover:bg-teal-500/20 text-teal-400 border border-teal-800 text-xs h-9 font-mono"
          data-testid="predict-excursion-btn">
          <Sparkle size={12} weight="duotone" className="mr-1.5" />
          {loadingPred ? "Predicting..." : "Predict Next 24 Hours"}
        </Button>

        {error && <p className="text-[10px] text-red-400 mt-2 font-mono" data-testid="predict-error">{error}</p>}

        {prediction && (
          <div className="mt-3 p-3 rounded-sm bg-zinc-950 border border-zinc-800 space-y-2" data-testid="prediction-result">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <TrendUp size={11} className="text-teal-400" />
                <span className="text-[10px] uppercase tracking-wider text-zinc-400">24h Forecast</span>
              </div>
              {prediction.prediction?.probability != null && (
                <span className={`text-xs font-mono font-bold ${prediction.prediction.excursion_likely ? "text-red-400" : "text-emerald-400"}`}>
                  {prediction.prediction.probability}%
                </span>
              )}
            </div>

            {prediction.prediction?.excursion_likely === true && (
              <div className="space-y-1 text-[11px]">
                <div className="flex items-center gap-1.5 text-red-400">
                  <WarningCircle size={11} />
                  <span>Excursion likely</span>
                </div>
                {prediction.prediction.predicted_time_hours != null && (
                  <p className="text-zinc-500">In ~{prediction.prediction.predicted_time_hours}h</p>
                )}
                {prediction.prediction.predicted_temp != null && (
                  <p className="text-zinc-500">Predicted temp: {prediction.prediction.predicted_temp}°F ({prediction.prediction.direction})</p>
                )}
              </div>
            )}
            {prediction.prediction?.excursion_likely === false && (
              <p className="text-[11px] text-emerald-400">No excursion expected</p>
            )}
            {prediction.prediction?.recommendations?.length > 0 && (
              <div className="pt-2 border-t border-zinc-800/50">
                <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Recommendations</p>
                <ul className="text-[10px] text-zinc-400 space-y-0.5 list-disc list-inside">
                  {prediction.prediction.recommendations.slice(0, 3).map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
            {prediction.prediction?.raw_response && (
              <p className="text-[10px] text-zinc-500 whitespace-pre-line">{prediction.prediction.raw_response}</p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
